import React, { useEffect, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Package, Truck, CheckCircle2, MapPin, Search, Clock, Box, AlertCircle, Phone } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const ICONS = {
    placed: Package,
    confirmed: CheckCircle2,
    packing: Box,
    packed: Box,
    shipped: Truck,
    delivered: CheckCircle2,
    cancelled: AlertCircle,
};
const STEPS = ["placed", "confirmed", "packing", "packed", "shipped", "delivered"];

export default function OrderTracking() {
    const { user } = useAuth();
    const { id: paramId } = useParams();
    const [search] = useSearchParams();
    const [orderId, setOrderId] = useState(paramId || search.get("order_id") || "");
    const [phone, setPhone] = useState(search.get("phone") || "");
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    // Auto-run for signed-in users on a /track/:id route
    useEffect(() => {
        if (paramId && user) run();
        // eslint-disable-next-line
    }, [paramId, user]);

    const run = async (e) => {
        e?.preventDefault();
        setError("");
        if (!orderId.trim()) return setError("Please enter an order ID");
        setBusy(true);
        try {
            let res;
            if (user) {
                try {
                    res = await api.get(`/orders/${orderId.trim()}/tracking`);
                } catch {
                    res = await api.get("/track", { params: { order_id: orderId.trim(), phone: phone.trim() } });
                }
            } else {
                res = await api.get("/track", { params: { order_id: orderId.trim(), phone: phone.trim() } });
            }
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.detail || "Could not find that order");
            setData(null);
        } finally { setBusy(false); }
    };

    return (
        <div className="bg-stone-50 min-h-screen py-12">
            <Toaster />
            <div className="max-w-3xl mx-auto px-4">
                <div className="text-center mb-8">
                    <div className="label-eyebrow text-sky-700">Order tracking</div>
                    <h1 className="font-serif text-4xl sm:text-5xl text-stone-900 mt-2">Where's my order?</h1>
                    <p className="text-sm text-stone-500 mt-2">Enter your order ID and phone number to see live shipment events.</p>
                </div>

                <form onSubmit={run} className="bg-white border border-stone-200 p-5 grid sm:grid-cols-[2fr,1.5fr,auto] gap-3" data-testid="track-form">
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-stone-500">Order ID</label>
                        <Input value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="e.g. 650E3269 (first 8) or full id" className="rounded-none border-stone-300 font-mono mt-1" data-testid="track-order-id" />
                    </div>
                    {!user && (
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-stone-500">Phone</label>
                            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit phone" className="rounded-none border-stone-300 mt-1" data-testid="track-phone" />
                        </div>
                    )}
                    <Button type="submit" disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 mt-auto h-10" data-testid="track-submit">
                        <Search className="w-4 h-4 mr-1" />{busy ? "Searching…" : "Track"}
                    </Button>
                </form>

                {error && (
                    <div className="bg-red-50 border border-red-200 p-4 mt-4 text-sm text-red-800" data-testid="track-error">
                        <AlertCircle className="inline w-4 h-4 mr-1 text-red-600" />{error}
                    </div>
                )}

                {data && (
                    <div className="bg-white border border-stone-200 mt-6 p-6" data-testid="track-result">
                        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-stone-500">Order</div>
                                <div className="font-mono font-semibold text-stone-900 text-lg">#{data.short_id}</div>
                                <div className="text-xs text-stone-500 mt-1">Placed {(data.created_at || "").slice(0, 10)}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-widest text-stone-500">Status</div>
                                <div className={`text-lg font-serif ${data.status === "delivered" ? "text-emerald-700" : data.status === "cancelled" ? "text-red-700" : "text-sky-700"}`}>{data.status_label}</div>
                            </div>
                        </div>

                        {/* Progress strip */}
                        {data.status !== "cancelled" && (
                            <div className="flex items-center gap-1 mb-6" data-testid="progress-strip">
                                {STEPS.map((s, i) => {
                                    const idx = STEPS.indexOf(data.status);
                                    const done = idx >= i;
                                    return (
                                        <React.Fragment key={s}>
                                            <div className={`flex flex-col items-center text-center min-w-[60px]`}>
                                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${done ? "bg-sky-700 text-white" : "bg-stone-200 text-stone-500"}`}>
                                                    {done ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                                                </div>
                                                <span className={`text-[10px] mt-1 ${done ? "text-sky-800" : "text-stone-400"}`}>{s.charAt(0).toUpperCase() + s.slice(1)}</span>
                                            </div>
                                            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${idx > i ? "bg-sky-700" : "bg-stone-200"}`} />}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        )}

                        {/* Shipment + courier */}
                        {(data.courier || data.tracking_no) && (
                            <div className="bg-sky-50 border border-sky-200 p-3 mb-6 grid sm:grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="label-eyebrow text-sky-700">Courier</div>
                                    <div className="font-medium text-stone-900">{data.courier || "—"}</div>
                                </div>
                                <div>
                                    <div className="label-eyebrow text-sky-700">Tracking #</div>
                                    <div className="font-mono text-stone-900">{data.tracking_no || "—"}</div>
                                </div>
                            </div>
                        )}

                        {/* Ship to + items */}
                        <div className="grid sm:grid-cols-2 gap-4 mb-6">
                            <div className="border border-stone-200 p-3">
                                <div className="label-eyebrow text-stone-500 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />Ship to</div>
                                <div className="text-sm text-stone-900 mt-1 font-medium">{data.shipped_to?.name || "—"}</div>
                                <div className="text-xs text-stone-600">{[data.shipped_to?.city, data.shipped_to?.state, data.shipped_to?.pincode].filter(Boolean).join(", ") || "—"}</div>
                            </div>
                            <div className="border border-stone-200 p-3">
                                <div className="label-eyebrow text-stone-500">Items · {data.total_items}</div>
                                <ul className="text-xs text-stone-700 mt-1 list-disc pl-5">
                                    {(data.items || []).map((it, i) => <li key={i}>{it.name} × {it.quantity}{it.size ? ` (${it.size})` : ""}</li>)}
                                </ul>
                            </div>
                        </div>

                        {/* Timeline */}
                        <div className="border-t border-stone-200 pt-4">
                            <div className="label-eyebrow text-stone-500 mb-3">Shipment timeline</div>
                            {(data.events || []).length === 0 ? (
                                <div className="text-xs text-stone-500 italic">No events recorded yet — we'll update this as your order moves.</div>
                            ) : (
                                <ol className="space-y-3">
                                    {[...(data.events || [])].reverse().map((e, i) => {
                                        const I = ICONS[e.status] || Clock;
                                        return (
                                            <li key={i} className="flex gap-3" data-testid={`event-${i}`}>
                                                <div className="w-7 h-7 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center mt-0.5">
                                                    <I className="w-3.5 h-3.5" />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium text-stone-900">{e.label}</div>
                                                    {e.note && <div className="text-xs text-stone-600">{e.note}</div>}
                                                    <div className="text-[10px] text-stone-400 mt-0.5">{(e.at || "").slice(0, 16).replace("T", " ")}</div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ol>
                            )}
                        </div>

                        <div className="border-t border-stone-200 pt-4 mt-6 text-[11px] text-stone-500 inline-flex items-center gap-1">
                            <Phone className="w-3 h-3" />Need help? Reach customer support via the live chat in the bottom corner.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Toaster() { return null; }  // not needed here, placeholder
